import DOMPurify from 'dompurify'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'

import NewsCard from '../../../../../features/news/components/NewsCard/NewsCard'
import { UnifiedNewsEntry } from '../../../../../features/news/types/UnifiedNewsEntry'
import { TranslationMap } from '../../../../../types/TranslationMap'
import { EditorialCardFormValues, EditorialCardLanguage, editorialCardLanguages, toPreviewNewsEntry } from '../../helpers/editorialCardHelpers'

type EditorialCardPreviewProps = {
  values: EditorialCardFormValues
  cardId?: string
}

// Media plugin embeds (e.g. YouTube) are iframes, which DOMPurify drops by default.
const sanitizeContent = (html: string) => DOMPurify.sanitize(html, { ADD_TAGS: ['iframe'], ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder'] })

const withSanitizedContent = (newsEntry: UnifiedNewsEntry): UnifiedNewsEntry => {
  const { entryData } = newsEntry.feedEntry
  if (!entryData.content) return newsEntry
  const content: TranslationMap = Object.fromEntries(Object.entries(entryData.content).map(([language, html]) => [language, sanitizeContent(html ?? '')]))
  return { ...newsEntry, feedEntry: { ...newsEntry.feedEntry, entryData: { ...entryData, content } } }
}

export const EditorialCardPreview: FC<EditorialCardPreviewProps> = ({ values, cardId }) => {
  const { t } = useTranslation()
  const [language, setLanguage] = useState<EditorialCardLanguage>('en')
  const newsEntry = useMemo(() => withSanitizedContent(toPreviewNewsEntry(values, language, cardId)), [values, language, cardId])

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="body2" fontWeight="bold">
          {t('internal-marketing:editorial_cards_preview')}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={language}
          onChange={(_, value: EditorialCardLanguage | null) => value && setLanguage(value)}
          aria-label={t('internal-marketing:editorial_cards_preview_language')}
        >
          {editorialCardLanguages.map((option) => (
            <ToggleButton key={option} value={option}>
              {option.toUpperCase()}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <NewsCard newsEntry={newsEntry} />
    </Box>
  )
}
